import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  LocationsService,
  OrdersService,
  VehiclesService,
} from '@boma/supplier-portal/shared/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { fetch } from '@nx/angular';
import { map, withLatestFrom } from 'rxjs/operators';
import * as OrdersActions from './orders.actions';

@Injectable()
export class OrdersEffects {
  loadOrders$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.loadOrders),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          const { orders } = state;
          console.log('loadOrders$', orders);
          return this.orderService
            .getOrders(
              user.selectedOrganizationId,
              orders.locationIds,
              orders.nextPage,
              orders.perPage,
              orders.query,
              orders.orderDirection,
              null,
              orders.customerIds,
              orders.dateRange,
              orders.statuses,
              orders.orderTypes
            )
            .pipe(
              map((response) =>
                OrdersActions.loadOrdersSuccess({ payload: response })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrdersFailure({ error });
        },
      })
    )
  );

  loadOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.loadOrderDetails),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .getOrder(user.selectedOrganizationId, action.id) // todo add the with notes parameter
            .pipe(
              map((order) =>
                OrdersActions.loadOrderDetailsSuccess({
                  order,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrderDetailsFailure({ error });
        },
      })
    )
  );

  updateOrderStatusToAccepted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.updateOrderStatusToAccepted),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .updateOrderStatusToAccepted(
              user.selectedOrganizationId,
              action.id,
              action.comment
            )
            .pipe(
              map(() =>
                OrdersActions.loadOrderDetails({ id: action.id })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrderDetailsFailure({ error });
        },
      })
    )
  );

  updateOrderStatusToProcessing$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.updateOrderStatusToProcessing),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .updateOrderStatusToProcessing(
              user.selectedOrganizationId,
              action.id,
              action.comment
            )
            .pipe(
              map(() =>
                OrdersActions.loadOrderDetails({ id: action.id })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrderDetailsFailure({ error });
        },
      })
    )
  );

  updateOrderStatusToLoading$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.updateOrderStatusToLoading),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .updateOrderStatusToLoading(
              user.selectedOrganizationId,
              action.id,
              action.comment
            )
            .pipe(
              map(() =>
                OrdersActions.loadOrderDetails({ id: action.id })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrderDetailsFailure({ error });
        },
      })
    )
  );

  updateOrderStatusToCancelled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.updateOrderStatusToCancelled),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .updateOrderStatusToCancelled(
              user.selectedOrganizationId,
              action.id,
              action.comment,
              action.direction
            )
            .pipe(
              map(() =>
                OrdersActions.loadOrderDetails({ id: action.id })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrderDetailsFailure({ error });
        },
      })
    )
  );

  updateOrderStatusToDispatched$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.updateOrderStatusToDispatched),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .updateOrderStatusToDispatched(
              user.selectedOrganizationId,
              action.id,
              action.comment,
              action.vehicleId,
              action.driverId
            )
            .pipe(
              map(() =>
                OrdersActions.loadOrderDetails({ id: action.id })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrderDetailsFailure({ error });
        },
      })
    )
  );

  updateOrderStatusToFulfilled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.updateOrderStatusToFulfilled),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .updateOrderStatusToFulfilled(
              user.selectedOrganizationId,
              action.id,
              action.comment,
              user.id,
              action.userPaidCashAmount
            )
            .pipe(
              map(() =>
                OrdersActions.loadOrderDetails({ id: action.id })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrderDetailsFailure({ error });
        },
      })
    )
  );

  loadLocations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.loadOrderLocations),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;

          const values: any = action.defaultOption
            ? [action.defaultOption]
            : [];

          if (action.locationType === 'Physical') {
            return this.locationService
              .loadLocations(
                user.selectedOrganizationId,
                '1',
                '25',
                action.query
              )
              .pipe(
                map((response) =>
                  OrdersActions.loadOrderLocationsSuccess({
                    payload: values.concat(response.items),
                  })
                )
              );
          } else {
            return this.vehicleService
              .loadVehicles(
                user.selectedOrganizationId,
                '1',
                '25',
                action.query
              )
              .pipe(
                map((response) =>
                  OrdersActions.loadOrderLocationsSuccess({
                    payload: values.concat(response.items),
                  })
                )
              );
          }
        },

        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrderLocationsFailure({ error });
        },
      })
    )
  );

  loadOrderMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.loadOrderMessages),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          const { orders } = state;
          return this.orderService
            .loadOrderMessages(
              user.selectedOrganizationId,
              action.id,
              action.direction
            )
            .pipe(
              map((messages) =>
                OrdersActions.loadOrderMessagesSuccess({
                  payload: messages,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrderMessagesFailure({ error });
        },
      })
    )
  );

  createOrderMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.createOrderMessage),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          const { orders } = state;
          return this.orderService
            .createOrderMessage(
              user.selectedOrganizationId,
              action.id,
              action.direction,
              action.content
            )
            .pipe(
              map((message) =>
                OrdersActions.createOrderMessageSuccess({
                  message,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.createOrderMessageFailure({ error });
        },
      })
    )
  );

  cancelFulfilledOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.cancelFulfilledOrder),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .cancelFulfilledOrder(
              user.selectedOrganizationId,
              action.id,
              action.reason || 'Other',
              action.restockLocation,
              action.comment,
              action.latitude,
              action.longitude
            )
            .pipe(
              map((order) =>
                OrdersActions.loadOrderDetailsSuccess({
                  order,
                })
              )
            );
        },
      })
    )
  );

  markChargeAsRefunded$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.markChargeAsRefunded),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .refundOrderCharge(
              user.selectedOrganizationId,
              action.id,
              action.chargeId,
            )
            .pipe(
              map((order) =>
                OrdersActions.loadOrderDetailsSuccess({
                  order,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrderDetailsFailure({ error });
        },
      })
    )
  );

  markOrderChargeAsPaid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.markOrderAsPaid),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .markOrderChargeStatusAsPaid(
              user.selectedOrganizationId,
              action.id,
              action.chargeId
            )
            .pipe(
              map((order) =>
                OrdersActions.loadOrderDetailsSuccess({
                  order,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrderDetailsFailure({ error });
        },
      })
    )
  );

  changePaymentMethod$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.changePaymentMethod),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .changeOrderChargePaymentMethod(
              user.selectedOrganizationId,
              action.id,
              action.chargeId,
              action.newPaymentMethod
            )
            .pipe(
              map((order) =>
                OrdersActions.loadOrderDetailsSuccess({
                  order,
                })
              )
            );
        },

        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrderDetailsFailure({ error });
        },
      })
    )
  );

  updateSupplyingLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.updateOrderSupplyingLocation),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .updateOrderSupplyingLocation({
              ...action,
              entityId: user.selectedOrganizationId,
            })
            .pipe(
              map((order) =>
                OrdersActions.loadOrderDetailsSuccess({
                  order,
                })
              )
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrderDetailsFailure({ error });
        },
      })
    )
  );

  loadOrderHandShake$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrdersActions.loadOrderHandShake),
      withLatestFrom(this.store),
      fetch({
        run: (action, state: any) => {
          const { user } = state;
          return this.orderService
            .getOrderHandshakeOffer(user.selectedOrganizationId, action.id)
            .pipe(
              map((handShakeOffer) =>
                OrdersActions.loadOrderHandShakeOfferSuccess({
                  handShakeOffer,
                })
              )
            );
        },
        onError: (action, error) => {
          console.error('Error', error);
          return OrdersActions.loadOrderHandShakeOfferFailure({ error });
        },
      })
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store,
    private orderService: OrdersService,
    private locationService: LocationsService,
    private vehicleService: VehiclesService,
    private router: Router
  ) {}
}
